/* eslint-disable no-console */
import useWebSocket from 'common/hooks/useWebSocket';
import { filter, sortBy, uniqBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

/**
 * Patient room messaging hook
 * This hook uses the existing WebSocket connection to emit patient-specific messages
 * It doesn't create a new connection, just manages patient room messaging
 */
const usePatientWebSocket = (patientId) => {
  const { socket } = useWebSocket();
  const [currentUsers, setCurrentUsers] = useState([]);

  useEffect(() => {
    // Join patient room when patientId changes and socket is connected
    if (socket?.connected) {
      socket.emit('join_patient_room', { patientId });

      socket.on('patient_room_joined', (data) => {
        if (data.patientId && data.currentUsers) {
          setCurrentUsers(uniqBy(sortBy(data.currentUsers, 'email'), 'email'));
        }
      });

      // Listen for user join/leave events
      socket.on('patient_room_user_joined', (data) => {
        if (data.patientId && data.user) {
          setCurrentUsers(uniqBy(sortBy([...currentUsers, data.user], 'email'), 'email'));
        }
      });

      // Listen for user_joined_patient_room event (broadcast to all users in room)
      socket.on('user_joined_patient_room', (data) => {
        if (data.patientId && data.allUsersInRoom) {
          setCurrentUsers(uniqBy(sortBy(data.allUsersInRoom, 'email'), 'email'));
        }
      });

      socket.on('patient_room_user_left', (data) => {
        if (data.patientId && data.userId) {
          setCurrentUsers(
            filter(data.allUsersInRoom, ({ userId }) => userId !== data.userId),
            'email'
          );
        }
      });

      // Listen for user_left_patient_room event (broadcast to all users in room)
      socket.on('user_left_patient_room', (data) => {
        if (data.patientId && data.remainingUsers) {
          setCurrentUsers(uniqBy(sortBy(data.remainingUsers, 'email'), 'email'));
        }
      });
    }

    // Cleanup: leave patient room when component unmounts
    return () => {
      if (socket?.connected) {
        socket.emit('leave_patient_room', { patientId });
        socket.off('user_left_patient_room');
        socket.off('patient_room_user_left');
        socket.off('user_joined_patient_room');
        socket.off('patient_room_user_joined');
        socket.off('patient_room_joined');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, socket, socket?.connected]); // intentionally not including currentUsers

  return useMemo(
    () => ({
      currentUsers,
    }),
    [currentUsers]
  );
};

export default usePatientWebSocket;
